import { Box, Tooltip, Typography } from '@mui/material';
import TaskFold from './TaskFold';
import { BLOCK_REASONS, formatTime, POST_SEND_BACK_REASONS, reasonLabel, SEND_BACK_REASONS, shortName } from './taskUi';

const ACTION_LABELS = {
    CREATED: 'started tracking',
    ASSIGNED: 'assigned',
    STARTED: 'started',
    COMPLETED: 'completed',
    AUTO_COMPLETED: 'completed (from editor)',
    REASSIGNED: 'reassigned',
    SENT_BACK: 'sent back',
    SKIPPED: 'skipped',
    BLOCKED: 'blocked',
    UNBLOCKED: 'unblocked',
    ON_HOLD: 'put on hold',
    RESUMED: 'resumed',
    COMMENTED: 'commented',
    FLAGGED_FOR_PM: 'flagged for PM',
    ACCESS_GRANTED: 'gave edit access',
    PROCESS_STAGE_REGRESSED: 'editor stage went backward',
    CONTENT_DELETED: 'file deleted',
    CONTENT_RESTORED: 'file restored',
    LANGUAGE_REMOVED: 'language removed',
    TASK_SYNC_FAILED: 'sync failed',
    OVERDUE_DETECTED: 'became overdue',
    IMPORTED: 'imported',
    CANCELLED: 'cancelled',
    TASK_COMPLETED: 'task completed',
    UPDATED: 'updated',
    EDITOR_SUBMIT_FAILED: "couldn't submit in the editor",
    FILE_ADDED: 'added a file',
    FILE_REPLACED: 'replaced a file',
    FILE_DELETED: 'deleted a file',
    AUDIO_CONVERTED: 'made dubbing audio',
    SRT_SAVED: 'saved an SRT',
    TEXT_EDITED: 'edited the text',
    FILE_LINKED: 'linked the file',
};

/** The audit trail, then this language's earlier tasks. Closed by default. */
const TaskHistory = ({ task, events, stages, earlierTasks }) => {
    const history = events.filter((e) => e.action !== 'COMMENTED');
    if (history.length === 0 && !earlierTasks?.length) return null;
    return (
        <TaskFold label={`History (${history.length})`}>
            <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {[...history].reverse().map((e) => (
                    <Box component="li" key={e.id} sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2">
                            <b>{e.actorType === 'SYSTEM' ? 'System' : shortName(e.actor)}</b>{' '}
                            {e.action === 'REASSIGNED' && !e.fromAssignee
                                ? ACTION_LABELS.ASSIGNED
                                : ACTION_LABELS[e.action] || e.action}
                            {e.stageKey && stages.find((s) => s.stageKey === e.stageKey)
                                ? ` · ${stages.find((s) => s.stageKey === e.stageKey).displayName}`
                                : ''}
                            {e.toAssignee && e.action !== 'ACCESS_GRANTED' ? ` → ${shortName(e.toAssignee)}` : ''}
                            {e.action === 'ACCESS_GRANTED' ? ` to ${shortName(e.toAssignee)}` : ''}
                        </Typography>
                        {e.reasonCode && (
                            <Typography variant="caption" color="text.secondary" component="div">
                                {reasonLabel([...SEND_BACK_REASONS, ...POST_SEND_BACK_REASONS, ...BLOCK_REASONS], e.reasonCode)}
                            </Typography>
                        )}
                        {e.editorEffect && (
                            <Typography variant="caption" color="text.secondary" component="div">
                                {e.editorEffect}
                            </Typography>
                        )}
                        {e.note && (
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                {e.note}
                            </Typography>
                        )}
                        <Tooltip title={new Date(e.timestamp).toString()}>
                            <Typography variant="caption" color="text.secondary">
                                {formatTime(e.timestamp)}
                            </Typography>
                        </Tooltip>
                    </Box>
                ))}
            </Box>
            {earlierTasks?.length > 0 && (
                <Box sx={{ pt: 1 }}>
                    <Typography variant="overline" component="div">
                        Earlier tasks for {task.languageLabel}
                    </Typography>
                    {earlierTasks.map((t) => (
                        <Typography key={t.id} variant="body2" color="text.secondary">
                            {t.overallStatus.toLowerCase()} · started {formatTime(t.createdAt)}
                        </Typography>
                    ))}
                </Box>
            )}
        </TaskFold>
    );
};

export default TaskHistory;
